/**
 * App state (zustand + persist). Local-first: everything works offline; the server adds
 * accounts, sync, journal cloud copies, live config and purchases.
 *
 * RULE: selectors must never return a fresh object/array (`?? []`, `.filter(...)`) — use the
 * EMPTY_* constants and derive inside useMemo, otherwise React re-renders forever.
 */
import { quietly } from '../lib/syncFlags';
import { useMemo } from 'react';
import { create } from 'zustand';
import { createJSONStorage, persist, type StateStorage } from 'zustand/middleware';
import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';
import { applyAward, DEFAULT_CONFIG, platformConfig, storeBillingOn, emptyProgress, foldLegacyCollect, LEGACY_COLLECT_KEY, mergeProgress, normalizeProgress, profileAccess, PATH_IDS, type PathId, type ArtStyleName, type AwardInput, type AwardType, type BillingPeriod, type CharacterSpec, type CollectionState, type Complimenter, type FeedbackType, type Feeling, type JournalFilter, type JournalLayout, type JournalMode, type ItemSticker, type PageSticker, migratePageStickers, type JournalTheme, type Lang, type LiveConfig, type ProgressState, type SharedMoment, type SkillId, type SparksReceipt, type SubjectCategory, type Tier, type DevicePolicy, type PendingPurchase, UNMANAGED_DEVICE, normalizeLocale, resolveLocale } from '@sketchling/core';
import { migrateForCurriculum, CONTENT } from '../lib/content';
import { billingPlatform } from '../lib/platform';

export type { Lang };

/* ----------------------------------- Types ----------------------------------- */

export interface LocalProfile {
  id: string;
  /** Server profile id once synced. */
  remoteId?: string;
  name: string;
  /** Avatar preset id (core AVATARS) — or 'custom' with `character`. */
  avatar: string;
  character?: CharacterSpec;
  age: number | null;
  signatureFont: string;
  favoriteSubjects: string[];
  isRealDrawer: boolean;
  createdAt: string;
}

export interface ExternalComment {
  id: string;
  commenter: Complimenter | 'teacher';
  name?: string;
  award?: AwardType;
  text: string;
  createdAt: string;
  seen: boolean;
}

export type JournalSource = 'lesson' | 'playground' | 'studio' | 'challenge' | 'upload' | 'drawTogether' | 'calibration' | 'skill';

export interface JournalItem {
  id: string;
  profileId: string;
  /** IndexedDB key of the photo (lib/idb). Empty when the drawing step was skipped. */
  imageKey: string;
  createdAt: string;
  title: string;
  description: string;
  feeling: Feeling | null;
  filter: JournalFilter;
  drawingId?: string;
  subject: string;
  category?: SubjectCategory;
  skill?: SkillId;
  source: JournalSource;
  feedback?: { type: FeedbackType; stars: 1 | 2 | 3; headline: string };
  moment?: SharedMoment;
  comments: ExternalComment[];
  /** Draw-together partner profile. */
  partnerProfileId?: string;
  remoteId?: string;
  shareId?: string;
  shareUrl?: string;
  /** Hearted in the journal (favourites filter). */
  favorite?: boolean;
  /** Stickers placed on this drawing (card coordinates), so they follow it across layouts, filters and the grid. */
  stickers?: ItemSticker[];
}

/** Legacy page-indexed sticker (migrated onto journal items when the state is loaded). */
export type PlacedSticker = PageSticker;

export interface JournalDecor {
  theme: JournalTheme;
  layout: JournalLayout;
  /** Legacy page stickers; always empty after migrateDecorStickers. */
  stickers: PlacedSticker[];
  /** User's choice of book / grid (falls back to the live config journalMode). */
  view?: JournalMode;
}

export interface Settings {
  language: Lang;
  sfx: boolean;
  music: boolean;
  /** Teacher voice-over (TTS). */
  voice: boolean;
  captions: boolean;
  reducedMotion: boolean;
  /** Battery saver (lib/power.ts): auto = on for low-end devices, low battery or sustained slow frames. */
  batterySaver: 'auto' | 'on' | 'off';
  /** Lesson playback speed (stroke animation). */
  speed: number;
  reminders: { enabled: boolean; days: number[]; time: string };
}

export interface Account {
  accessToken?: string;
  refreshToken?: string;
  userId?: string;
  email?: string | null;
  anonymous?: boolean;
  /** Server-side tester flag (demo account): ignore progress locks. */
  unlockAll?: boolean;
}

export interface Entitlement {
  tier: Tier;
  period?: BillingPeriod;
  trialEndsAt?: string;
  expiresAt?: string;
  source?: 'server' | 'local-mock' | 'dev';
  /** Individual plan: the one full-access profile (local id). */
  premiumProfileId?: string;
}

export interface Onboarding {
  done: boolean;
  /** Is a grown-up sitting next to the child? */
  grownUp: 'yes' | 'no' | null;
  consent: boolean;
  /** Analytics consent (off until a parent accepts). */
  analyticsConsent: boolean;
  answers: Record<string, string | string[]>;
  calibration: 'great' | 'notRound' | 'unequal' | 'tooSmall' | 'noDrawing' | 'skipped' | null;
  paywallSeen: boolean;
  step: string | null;
}

export interface Cheats {
  unlocked: boolean;
  unlockAllPaths: boolean;
  forcePremium: boolean;
  alwaysReview: boolean;
  alwaysOnboarding: boolean;
  mockPurchase: boolean;
  /** Shifts "now" for time-based features (letters, stories, challenges). */
  dateOffsetDays: number;
  debugBuckets: Record<string, string>;
  showAllItems: boolean;
  /** Dev override of the live-config illustration skin (null = follow the config). */
  artStyle: ArtStyleName | null;
  /** Platform-service simulators (web / dev): managed device, purchases off, Ask to Buy, update gate. */
  simManaged: boolean;
  simSupervised: boolean;
  simPurchasesOff: boolean;
  simAskToBuy: boolean;
  simUpdate: 'none' | 'optional' | 'force';
}

export type AppUpdateLevel = 'none' | 'optional' | 'force';

export interface AnalyticsEvent {
  name: string;
  props?: Record<string, unknown>;
  at: string;
}

interface AppState {
  profiles: LocalProfile[];
  activeProfileId: string | null;
  progress: Record<string, ProgressState>;
  journal: JournalItem[];
  decor: Record<string, JournalDecor>;
  settings: Settings;
  account: Account;
  entitlement: Entitlement;
  onboarding: Onboarding;
  cheats: Cheats;
  config: LiveConfig;
  buckets: Record<string, string>;
  lastReviewVersion: string | null;
  analytics: AnalyticsEvent[];
  /** Device install / first launch (for the update gate + migrations). */
  firstLaunch: string;
  /** Ask to Buy: a purchase waiting for a parent's approval. */
  pendingPurchase: PendingPurchase | null;
  /** Server app-update requirement for this build (GET /config). */
  appUpdate: AppUpdateLevel;
  /** Native device-policy probe (managed / supervised / purchases allowed). */
  devicePolicy: DevicePolicy;

  updateSettings(s: Partial<Settings>): void;
  addProfile(p: Omit<LocalProfile, 'id' | 'createdAt'> & { id?: string }): LocalProfile;
  updateProfile(id: string, p: Partial<LocalProfile>): void;
  removeProfile(id: string): void;
  switchProfile(id: string): void;
  /** Generic progress update for one profile (use core pure functions inside `fn`). */
  updateProgress(profileId: string, fn: (p: ProgressState) => ProgressState): void;
  /** Apply an award to a profile (default: active) and return the celebration receipt. */
  award(input: AwardInput, profileId?: string): SparksReceipt;
  mergeRemoteProgress(profileId: string, remote: ProgressState): void;
  /** Update a profile's synced collection state (`progress.collect`); a no-op when `fn` returns the same object. */
  updateCollect(profileId: string, fn: (c: CollectionState) => CollectionState): void;
  /** Merge the legacy local-only collect store JSON into every profile's progress (one-time migration). */
  importLegacyCollect(raw: string): void;
  addJournalItem(item: JournalItem): void;
  updateJournalItem(id: string, patch: Partial<JournalItem>): void;
  removeJournalItem(id: string): void;
  setDecor(profileId: string, patch: Partial<JournalDecor>): void;
  setAccount(a: Account): void;
  setEntitlement(e: Entitlement): void;
  setOnboarding(o: Partial<Onboarding>): void;
  setCheats(c: Partial<Cheats>): void;
  setConfig(config: LiveConfig, buckets: Record<string, string>): void;
  setLastReviewVersion(v: string): void;
  setPendingPurchase(p: PendingPurchase | null): void;
  setAppUpdate(g: AppUpdateLevel): void;
  setDevicePolicy(p: DevicePolicy): void;
  logEvent(name: string, props?: Record<string, unknown>): void;
  resetAll(): void;
}

/* --------------------------------- Constants --------------------------------- */

/** Stable empty values for selectors. */
export const EMPTY_PROGRESS: ProgressState = emptyProgress();
export const EMPTY_COLLECTION: CollectionState = EMPTY_PROGRESS.collect;
export const EMPTY_LIST: never[] = [];
export const EMPTY_DECOR: JournalDecor = { theme: 'classic', layout: 2, stickers: [] };

export const uid = () => Math.random().toString(36).slice(2, 10) + Date.now().toString(36);

export const defaultSettings: Settings = {
  // Device / browser languages → the first available locale (zh-TW → zh-Hans until zh-Hant ships, else English).
  language: resolveLocale(typeof navigator !== 'undefined' ? (navigator.languages?.length ? navigator.languages : [navigator.language]) : []),
  sfx: true,
  music: false,
  voice: true,
  captions: true,
  reducedMotion: false,
  batterySaver: 'auto',
  speed: 1,
  reminders: { enabled: false, days: [1, 3, 5], time: '17:00' },
};

const defaultOnboarding: Onboarding = {
  done: false,
  grownUp: null,
  consent: false,
  analyticsConsent: false,
  answers: {},
  calibration: null,
  paywallSeen: false,
  step: null,
};

const defaultCheats: Cheats = {
  unlocked: false,
  unlockAllPaths: false,
  forcePremium: false,
  alwaysReview: false,
  alwaysOnboarding: false,
  mockPurchase: true,
  dateOffsetDays: 0,
  debugBuckets: {},
  showAllItems: false,
  artStyle: null,
  simManaged: false,
  simSupervised: false,
  simPurchasesOff: false,
  simAskToBuy: false,
  simUpdate: 'none',
};

const initial = () => ({
  profiles: [] as LocalProfile[],
  activeProfileId: null as string | null,
  progress: {} as Record<string, ProgressState>,
  journal: [] as JournalItem[],
  decor: {} as Record<string, JournalDecor>,
  settings: defaultSettings,
  account: {} as Account,
  entitlement: { tier: 'free' } as Entitlement,
  onboarding: defaultOnboarding,
  cheats: defaultCheats,
  config: platformConfig(DEFAULT_CONFIG, billingPlatform),
  buckets: {} as Record<string, string>,
  lastReviewVersion: null as string | null,
  analytics: [] as AnalyticsEvent[],
  firstLaunch: new Date().toISOString(),
  pendingPurchase: null as PendingPurchase | null,
  appUpdate: 'none' as AppUpdateLevel,
  devicePolicy: UNMANAGED_DEVICE as DevicePolicy,
});

/**
 * Migration: stickers used to be stored per book page index, so they jumped to other drawings when the
 * layout or a filter changed. Move each one onto the drawing it was sitting on (by journal item id).
 */
export function migrateDecorStickers(journal: JournalItem[], decor: Record<string, JournalDecor>): { journal: JournalItem[]; decor: Record<string, JournalDecor> } {
  const legacy = Object.entries(decor ?? {}).filter(([, d]) => Array.isArray(d?.stickers) && d.stickers.length);
  if (!legacy.length) return { journal, decor };
  const nextDecor = { ...decor };
  const add: Record<string, ItemSticker[]> = {};
  for (const [profileId, d] of legacy) {
    const items = journal.filter((j) => j.profileId === profileId);
    Object.assign(add, migratePageStickers(items, d.layout ?? 2, d.stickers));
    nextDecor[profileId] = { ...d, stickers: [] };
  }
  return { journal: journal.map((j) => (add[j.id] ? { ...j, stickers: [...(j.stickers ?? []), ...add[j.id]!] } : j)), decor: nextDecor };
}

/* ---------------------------------- Storage ---------------------------------- */

/** Capacitor Preferences on native (survives WebView storage eviction), localStorage on web. */
const storage: StateStorage = Capacitor.isNativePlatform()
  ? {
      getItem: async (k) => (await Preferences.get({ key: k })).value,
      setItem: (k, v) => Preferences.set({ key: k, value: v }),
      removeItem: (k) => Preferences.remove({ key: k }),
    }
  : {
      getItem: (k) => localStorage.getItem(k),
      setItem: (k, v) => {
        try {
          localStorage.setItem(k, v);
        } catch {
          /* quota: images live in IndexedDB, so this should not happen */
        }
      },
      removeItem: (k) => localStorage.removeItem(k),
    };

export const STORAGE_KEY = 'sketchling-state-v1';

/* ----------------------------------- Store ----------------------------------- */

export const useApp = create<AppState>()(
  persist(
    (set, get) => ({
      ...initial(),

      updateSettings: (s) => set({ settings: { ...get().settings, ...s } }),

      addProfile: (p) => {
        const profile: LocalProfile = { ...p, id: p.id ?? uid(), createdAt: new Date().toISOString() };
        set((st) => ({
          profiles: [...st.profiles, profile],
          progress: { ...st.progress, [profile.id]: emptyProgress() },
          activeProfileId: st.activeProfileId ?? profile.id,
        }));
        return profile;
      },
      updateProfile: (id, p) => set((st) => ({ profiles: st.profiles.map((x) => (x.id === id ? { ...x, ...p } : x)) })),
      removeProfile: (id) =>
        set((st) => {
          const profiles = st.profiles.filter((x) => x.id !== id);
          const progress = { ...st.progress };
          delete progress[id];
          return {
            profiles,
            progress,
            journal: st.journal.filter((j) => j.profileId !== id),
            activeProfileId: st.activeProfileId === id ? (profiles[0]?.id ?? null) : st.activeProfileId,
          };
        }),
      switchProfile: (id) =>
        set((st) => {
          const pref = st.progress[id]?.collect.prefs?.locale?.v;
          const language = pref ? normalizeLocale(pref) : st.settings.language;
          return { activeProfileId: id, ...(language !== st.settings.language ? { settings: { ...st.settings, language } } : {}) };
        }),

      updateProgress: (profileId, fn) =>
        set((st) => ({ progress: { ...st.progress, [profileId]: fn(st.progress[profileId] ?? emptyProgress()) } })),

      award: (input, profileId) => {
        const id = profileId ?? get().activeProfileId;
        if (!id) throw new Error('no_active_profile');
        const { state, receipt } = applyAward(get().progress[id] ?? emptyProgress(), input);
        set((st) => ({ progress: { ...st.progress, [id]: state } }));
        get().logEvent('Sparks Earned', { sparks: receipt.sparks, total: receipt.sparksAfter, kind: input.kind });
        return receipt;
      },

      // Applied quietly: the merged answer of the server is not queued back to it (lib/syncQueue.ts).
      // Unchanged merges (the usual answer to our own push) are not written: no re-render, no persist.
      mergeRemoteProgress: (profileId, remote) =>
        quietly(() =>
          set((st) => {
            const cur = st.progress[profileId] ?? emptyProgress();
            const next = migrateForCurriculum(mergeProgress(cur, normalizeProgress(remote)));
            const same = (a: ProgressState) => JSON.stringify({ ...a, updatedAt: '' });
            return same(next) === same(cur) ? {} : { progress: { ...st.progress, [profileId]: next } };
          }),
        ),

      updateCollect: (profileId, fn) =>
        set((st) => {
          const p = st.progress[profileId] ?? emptyProgress();
          const collect = fn(p.collect);
          return collect === p.collect ? {} : { progress: { ...st.progress, [profileId]: { ...p, collect } } };
        }),

      importLegacyCollect: (raw) =>
        set((st) => {
          const progress = foldLegacyCollect(st.progress, raw);
          return progress === st.progress ? {} : { progress };
        }),

      addJournalItem: (item) => set((st) => ({ journal: [item, ...st.journal] })),
      updateJournalItem: (id, patch) => set((st) => ({ journal: st.journal.map((j) => (j.id === id ? { ...j, ...patch } : j)) })),
      removeJournalItem: (id) => set((st) => ({ journal: st.journal.filter((j) => j.id !== id) })),
      setDecor: (profileId, patch) => set((st) => ({ decor: { ...st.decor, [profileId]: { ...(st.decor[profileId] ?? EMPTY_DECOR), ...patch } } })),

      setAccount: (a) => set({ account: a }),
      setEntitlement: (e) => set({ entitlement: e }),
      setOnboarding: (o) => set({ onboarding: { ...get().onboarding, ...o } }),
      setCheats: (c) => set({ cheats: { ...get().cheats, ...c } }),
      setConfig: (config, buckets) => set({ config: platformConfig(config, billingPlatform), buckets }),
      setLastReviewVersion: (v) => set({ lastReviewVersion: v }),
      setPendingPurchase: (p) => set({ pendingPurchase: p }),
      setAppUpdate: (g) => set({ appUpdate: g }),
      setDevicePolicy: (p) => set({ devicePolicy: p }),
      logEvent: (name, props) => set((st) => ({ analytics: [...st.analytics.slice(-199), { name, props, at: new Date().toISOString() }] })),
      resetAll: () => set({ ...initial(), settings: get().settings }),
    }),
    {
      name: STORAGE_KEY,
      version: 1,
      storage: createJSONStorage(() => storage),
      partialize: (s) => ({
        profiles: s.profiles,
        activeProfileId: s.activeProfileId,
        progress: s.progress,
        journal: s.journal,
        decor: s.decor,
        settings: s.settings,
        account: s.account,
        entitlement: s.entitlement,
        onboarding: s.onboarding,
        cheats: s.cheats,
        config: s.config,
        buckets: s.buckets,
        lastReviewVersion: s.lastReviewVersion,
        analytics: s.analytics,
        firstLaunch: s.firstLaunch,
        pendingPurchase: s.pendingPurchase,
      }),
      // One-time migration of the legacy local-only collect store into synced progress (after hydration,
      // so the folded state is persisted; the old key is kept as a backup under `<key>.migrated`).
      onRehydrateStorage: () => (state) => {
        if (state) migrateLegacyCollect(state.importLegacyCollect);
      },
      merge: (persisted, current) => {
        const p = (persisted ?? {}) as Partial<AppState>;
        return {
          ...current,
          ...p,
          settings: { ...defaultSettings, ...(p.settings ?? {}), language: p.settings?.language ? normalizeLocale(p.settings.language) : defaultSettings.language },
          onboarding: { ...defaultOnboarding, ...(p.onboarding ?? {}) },
          cheats: { ...defaultCheats, ...(p.cheats ?? {}) },
          config: platformConfig({ ...DEFAULT_CONFIG, ...(p.config ?? {}), onboarding: { ...DEFAULT_CONFIG.onboarding, ...(p.config?.onboarding ?? {}) } }, billingPlatform),
          progress: Object.fromEntries(Object.entries(p.progress ?? {}).map(([k, v]) => [k, migrateForCurriculum(normalizeProgress(v))])),
          ...migrateDecorStickers(p.journal ?? current.journal, p.decor ?? current.decor),
        };
      },
    },
  ),
);

/** Fold `sketchling-collect-v1` (localStorage, where the old store lived on every platform) into progress, then retire the key. */
export function migrateLegacyCollect(importer: (raw: string) => void): void {
  try {
    const raw = localStorage.getItem(LEGACY_COLLECT_KEY);
    if (raw === null) return;
    importer(raw);
    localStorage.setItem(`${LEGACY_COLLECT_KEY}.migrated`, raw);
    localStorage.removeItem(LEGACY_COLLECT_KEY);
  } catch {
    /* storage unavailable: try again on the next launch (the fold is idempotent) */
  }
}

/* ---------------------------------- Hooks ---------------------------------- */

export function useActiveProfile(): LocalProfile | undefined {
  const profiles = useApp((s) => s.profiles);
  const id = useApp((s) => s.activeProfileId);
  return useMemo(() => profiles.find((p) => p.id === id), [profiles, id]);
}

/** Synced collection state of the active profile (stable EMPTY_COLLECTION when none). */
export function useCollection(): CollectionState {
  return useApp((s) => (s.activeProfileId ? (s.progress[s.activeProfileId]?.collect ?? EMPTY_COLLECTION) : EMPTY_COLLECTION));
}

/** Progress of the active profile (stable EMPTY_PROGRESS when none). */
export function useProgress(): ProgressState {
  return useApp((s) => (s.activeProfileId ? (s.progress[s.activeProfileId] ?? EMPTY_PROGRESS) : EMPTY_PROGRESS));
}

/** Premium access + unlock-all for the active profile. */
/** Does this platform sell (live config `storeBilling`, see core `storeBillingOn`)? */
export const selectStoreBilling = (s: Pick<AppState, 'config'>): boolean => storeBillingOn(s.config, billingPlatform);
/** Plan tier the app behaves as: a platform that sells nothing (Android v1) gives everyone the full content (Family). */
export const selectTier = (s: Pick<AppState, 'config' | 'entitlement'>): Tier => (selectStoreBilling(s) ? s.entitlement.tier : 'family');
export const useStoreBilling = (): boolean => useApp(selectStoreBilling);

export function useAccess(): { premium: boolean; unlockAll: boolean; tier: Tier; freeBoundary: LiveConfig['freeBoundary']; favouritePath: PathId | null; pictureMinLevel: number } {
  const tier = useApp(selectTier);
  const premiumProfileId = useApp((s) => s.entitlement.premiumProfileId);
  const profiles = useApp((s) => s.profiles);
  const id = useApp((s) => s.activeProfileId);
  // Cheats only count in dev builds (a persisted cheat must never unlock anything in a store build).
  const forcePremium = useApp((s) => import.meta.env.DEV && s.cheats.forcePremium);
  const cheatUnlock = useApp((s) => import.meta.env.DEV && s.cheats.unlockAllPaths);
  const serverUnlock = useApp((s) => !!s.account.unlockAll);
  // Monetization P0-6: remote free boundary + the profile's favourite world (first favourite subject that is a world).
  const freeBoundary = useApp((s) => s.config.freeBoundary);
  const pictureMinLevel = useApp((s) => s.config.pictureMinLevel ?? 2);
  return useMemo(() => {
    const access = id ? profileAccess(tier, profiles.map((p) => p.id), id, premiumProfileId) : 'limited';
    const fav = profiles.find((p) => p.id === id)?.favoriteSubjects.find((c) => c !== 'intro' && (PATH_IDS as string[]).includes(c));
    return { premium: forcePremium || access === 'full', unlockAll: cheatUnlock || serverUnlock, tier, freeBoundary, favouritePath: (fav as PathId | undefined) ?? null, pictureMinLevel };
  }, [tier, premiumProfileId, profiles, id, forcePremium, cheatUnlock, serverUnlock, freeBoundary, pictureMinLevel]);
}

/** "Now", shifted by the cheats date override. */
export function appNow(): Date {
  const off = useApp.getState().cheats.dateOffsetDays;
  return new Date(Date.now() + off * 86400_000);
}

/** Journal items of the active profile (memoised). */
export function useJournal(): JournalItem[] {
  const journal = useApp((s) => s.journal);
  const id = useApp((s) => s.activeProfileId);
  return useMemo(() => journal.filter((j) => j.profileId === id), [journal, id]);
}

export function useDecor(): JournalDecor {
  return useApp((s) => (s.activeProfileId ? (s.decor[s.activeProfileId] ?? EMPTY_DECOR) : EMPTY_DECOR));
}

export const APP_VERSION = '1.0.0';
export const APP_BUILD = 11;
